import { Koi } from '@ksyos/koi';
import { getTextInputStringSchema } from '@common/validation/utils/getTextInputStringSchema';

type MedicationCalciumAntagonistItems = {
    amlodipine: boolean;
    barnidipine: boolean;
    diltiazem: boolean;
    lercanidipine: boolean;
    nifedipine: boolean;
    verapamil: boolean;
};

type MedicationDiureticaItems = {
    amiloride: boolean;
    bumetanide: boolean;
    eplerenone: boolean;
    furosemide: boolean;
    hydrochloorthiazide: boolean;
    spironolacton: boolean;
    triamtereen: boolean;
};

export type CardiologyMedicationItems = {
    aceRemmer: boolean;
    aldosteronAntagonist: boolean;
    antiAritmica: boolean;
    betablokker: boolean;
    calciumAntagonist: boolean;
    diuretica: boolean;
    oraleAntistolling: boolean;
    overige: string | null;
    plaatjesAggregatieRemmers: boolean;
};

export type CardiologyMedication = {
    calciumAntagonistType: MedicationCalciumAntagonistItems;
    diureticaType: MedicationDiureticaItems;
    general: CardiologyMedicationItems;
};

export enum CardiologyMedicationLabel {
    aceRemmer = 'ACE-remmer, Angiotensine Receptor Blokker',
    aldosteronAntagonist = 'Aldosteron-antagonist',
    antiAritmica = 'Anti-aritmicum (o.a. flecainide, propafenone, sotalol, amiodarone)',
    betablokker = 'Bètablokker',
    calciumAntagonist = 'Calcium antagonist, namelijk...',
    diuretica = 'Diureticum, namelijk...',
    oraleAntistolling = 'Orale antistolling (DOAC, VKA)',
    overige = 'Anders, namelijk...',
    plaatjesAggregatieRemmers = 'Plaatjes aggregatieremmers (o.a. aspirine, clopidogrel)',
}

export const cardiologyMedicationLabels = [
    CardiologyMedicationLabel.aceRemmer,
    CardiologyMedicationLabel.betablokker,
    CardiologyMedicationLabel.calciumAntagonist,
    CardiologyMedicationLabel.diuretica,
    CardiologyMedicationLabel.aldosteronAntagonist,
    CardiologyMedicationLabel.plaatjesAggregatieRemmers,
    CardiologyMedicationLabel.oraleAntistolling,
    CardiologyMedicationLabel.antiAritmica,
    CardiologyMedicationLabel.overige,
];

export enum CalciumAntagonistDihydropyridinesMedicationLabel {
    amlodipine = 'Amlodipine',
    barnidipine = 'Barnidipine',
    lercanidipine = 'Lercanidipine',
    nifedipine = 'Nifedipine',
}

export const calciumAntagonistDihydropyridinesMedicationLabels = [
    CalciumAntagonistDihydropyridinesMedicationLabel.amlodipine,
    CalciumAntagonistDihydropyridinesMedicationLabel.barnidipine,
    CalciumAntagonistDihydropyridinesMedicationLabel.lercanidipine,
    CalciumAntagonistDihydropyridinesMedicationLabel.nifedipine,
];

export enum CalciumAntagonistNonDihydropyridinesMedicationLabel {
    diltiazem = 'Diltiazem',
    verapamil = 'Verapamil',
}

export const calciumAntagonistNonDihydropyridinesMedicationLabels = [
    CalciumAntagonistNonDihydropyridinesMedicationLabel.diltiazem,
    CalciumAntagonistNonDihydropyridinesMedicationLabel.verapamil,
];

export enum DiureticaMedicationLabel {
    amiloride = 'Amiloride',
    bumetanide = 'Bumetanide',
    eplerenone = 'Eplerenone',
    furosemide = 'Furosemide',
    hydrochloorthiazide = 'Hydrochloorthiazide',
    spironolacton = 'Spironolacton',
    triamtereen = 'Triamtereen',
}

export const diureticaMedicationLabels = [
    DiureticaMedicationLabel.hydrochloorthiazide,
    DiureticaMedicationLabel.furosemide,
    DiureticaMedicationLabel.bumetanide,
    DiureticaMedicationLabel.amiloride,
    DiureticaMedicationLabel.spironolacton,
    DiureticaMedicationLabel.triamtereen,
    DiureticaMedicationLabel.eplerenone,
];

export const initialMedicationData: CardiologyMedication = {
    calciumAntagonistType: {
        amlodipine: false,
        barnidipine: false,
        diltiazem: false,
        lercanidipine: false,
        nifedipine: false,
        verapamil: false,
    },
    diureticaType: {
        amiloride: false,
        bumetanide: false,
        eplerenone: false,
        furosemide: false,
        hydrochloorthiazide: false,
        spironolacton: false,
        triamtereen: false,
    },
    general: {
        aceRemmer: false,
        aldosteronAntagonist: false,
        antiAritmica: false,
        betablokker: false,
        calciumAntagonist: false,
        diuretica: false,
        oraleAntistolling: false,
        overige: null,
        plaatjesAggregatieRemmers: false,
    },
};

const calciumAntagonistSchema = Koi.object({
    amlodipine: Koi.boolean(),
    barnidipine: Koi.boolean(),
    diltiazem: Koi.boolean(),
    lercanidipine: Koi.boolean(),
    nifedipine: Koi.boolean(),
    verapamil: Koi.boolean(),
});

const diureticaSchema = Koi.object({
    amiloride: Koi.boolean(),
    bumetanide: Koi.boolean(),
    eplerenone: Koi.boolean(),
    furosemide: Koi.boolean(),
    hydrochloorthiazide: Koi.boolean(),
    spironolacton: Koi.boolean(),
    triamtereen: Koi.boolean(),
});

const medicationSchema = Koi.object({
    aceRemmer: Koi.boolean(),
    aldosteronAntagonist: Koi.boolean(),
    antiAritmica: Koi.boolean(),
    betablokker: Koi.boolean(),
    calciumAntagonist: Koi.boolean(),
    diuretica: Koi.boolean(),
    oraleAntistolling: Koi.boolean(),
    overige: getTextInputStringSchema(true, 255),
    plaatjesAggregatieRemmers: Koi.boolean(),
});

export const medicationDataDraftSchema = Koi.object({
    calciumAntagonistType: calciumAntagonistSchema,
    diureticaType: diureticaSchema,
    general: medicationSchema,
});

export const medicationDataFinalSchema = Koi.object({
    calciumAntagonistType: Koi.when('general.calciumAntagonist', {
        is: true,
        otherwise: calciumAntagonistSchema,
        then: calciumAntagonistSchema.anyFilledIn(),
    }),
    diureticaType: Koi.when('general.diuretica', {
        is: true,
        otherwise: diureticaSchema,
        then: diureticaSchema.anyFilledIn(),
    }),
    general: Koi.when('...patientUsesMedication', {
        is: true,
        otherwise: medicationSchema,
        then: medicationSchema.anyFilledIn(),
    }),
});
